/**
 * Iranian bank directory for DISPLAY only (owner request 2026-10-12: «نام بانک در دسترس نیست» → show the
 * bank). The issuer is derived from public, deterministic data: the card BIN (first 6 digits, Shetab)
 * and the 3-digit bank code inside a Sheba number (IR + 2 check digits + bank code). It never decides
 * ownership or eligibility — card/IBAN ownership is still proven only by the bank-identity provider.
 *
 * Banks merged into Sepah (Ansar, Hekmat, Kowsar, Mehr Eghtesad, Ghavamin) and Ayandeh (into Melli) keep
 * their old BINs on cards still in circulation; they map to the bank that now holds the account.
 */
export type BankCode =
  | 'MELLI'
  | 'SEPAH'
  | 'MELLAT'
  | 'TEJARAT'
  | 'SADERAT'
  | 'REFAH'
  | 'MASKAN'
  | 'KESHAVARZI'
  | 'SANAT_MADAN'
  | 'TOSEE_SADERAT'
  | 'POST'
  | 'TOSEE_TAAVON'
  | 'KARAFARIN'
  | 'PARSIAN'
  | 'EGHTESAD_NOVIN'
  | 'SAMAN'
  | 'PASARGAD'
  | 'SARMAYEH'
  | 'SINA'
  | 'MEHR_IRAN'
  | 'SHAHR'
  | 'GARDESHGARI'
  | 'DAY'
  | 'IRAN_ZAMIN'
  | 'RESALAT'
  | 'MELAL'
  | 'KHAVARMIANEH'
  | 'NOOR'
  | 'TOSEE'
  | 'MARKAZI'
  | 'IRAN_VENEZUELA';

export const BANK_NAMES: Record<BankCode, string> = {
  MELLI: 'بانک ملی',
  SEPAH: 'بانک سپه',
  MELLAT: 'بانک ملت',
  TEJARAT: 'بانک تجارت',
  SADERAT: 'بانک صادرات',
  REFAH: 'بانک رفاه کارگران',
  MASKAN: 'بانک مسکن',
  KESHAVARZI: 'بانک کشاورزی',
  SANAT_MADAN: 'بانک صنعت و معدن',
  TOSEE_SADERAT: 'بانک توسعه صادرات',
  POST: 'پست بانک',
  TOSEE_TAAVON: 'بانک توسعه تعاون',
  KARAFARIN: 'بانک کارآفرین',
  PARSIAN: 'بانک پارسیان',
  EGHTESAD_NOVIN: 'بانک اقتصاد نوین',
  SAMAN: 'بانک سامان',
  PASARGAD: 'بانک پاسارگاد',
  SARMAYEH: 'بانک سرمایه',
  SINA: 'بانک سینا',
  MEHR_IRAN: 'بانک قرض‌الحسنه مهر ایران',
  SHAHR: 'بانک شهر',
  GARDESHGARI: 'بانک گردشگری',
  DAY: 'بانک دی',
  IRAN_ZAMIN: 'بانک ایران زمین',
  RESALAT: 'بانک قرض‌الحسنه رسالت',
  MELAL: 'مؤسسه اعتباری ملل',
  KHAVARMIANEH: 'بانک خاورمیانه',
  NOOR: 'مؤسسه اعتباری نور',
  TOSEE: 'مؤسسه اعتباری توسعه',
  MARKAZI: 'بانک مرکزی',
  IRAN_VENEZUELA: 'بانک ایران و ونزوئلا',
};

const BINS: Record<string, BankCode> = {
  '603799': 'MELLI',
  '636214': 'MELLI', // Ayandeh (merged into Melli)
  '589210': 'SEPAH',
  '627381': 'SEPAH', // Ansar
  '636949': 'SEPAH', // Hekmat Iranian
  '505801': 'SEPAH', // Kowsar
  '639370': 'SEPAH', // Mehr Eghtesad
  '639599': 'SEPAH', // Ghavamin
  '610433': 'MELLAT',
  '991975': 'MELLAT',
  '627353': 'TEJARAT',
  '585983': 'TEJARAT',
  '603769': 'SADERAT',
  '589463': 'REFAH',
  '628023': 'MASKAN',
  '603770': 'KESHAVARZI',
  '639217': 'KESHAVARZI',
  '627961': 'SANAT_MADAN',
  '627648': 'TOSEE_SADERAT',
  '207177': 'TOSEE_SADERAT',
  '627760': 'POST',
  '502908': 'TOSEE_TAAVON',
  '627488': 'KARAFARIN',
  '502910': 'KARAFARIN',
  '622106': 'PARSIAN',
  '639194': 'PARSIAN',
  '627884': 'PARSIAN',
  '627412': 'EGHTESAD_NOVIN',
  '621986': 'SAMAN',
  '502229': 'PASARGAD',
  '639347': 'PASARGAD',
  '639607': 'SARMAYEH',
  '639346': 'SINA',
  '606373': 'MEHR_IRAN',
  '502806': 'SHAHR',
  '504706': 'SHAHR',
  '505416': 'GARDESHGARI',
  '502938': 'DAY',
  '505785': 'IRAN_ZAMIN',
  '504172': 'RESALAT',
  '606256': 'MELAL',
  '585947': 'KHAVARMIANEH',
  '507677': 'NOOR',
  '628157': 'TOSEE',
  '636795': 'MARKAZI',
  '581874': 'IRAN_VENEZUELA',
};

/** Sheba bank codes (digits 5–7 of IR + 24 digits). */
const IBAN_CODES: Record<string, BankCode> = {
  '010': 'MARKAZI',
  '011': 'SANAT_MADAN',
  '012': 'MELLAT',
  '013': 'REFAH',
  '014': 'MASKAN',
  '015': 'SEPAH',
  '016': 'KESHAVARZI',
  '017': 'MELLI',
  '018': 'TEJARAT',
  '019': 'SADERAT',
  '020': 'TOSEE_SADERAT',
  '021': 'POST',
  '022': 'TOSEE_TAAVON',
  '051': 'TOSEE',
  '052': 'SEPAH', // Ghavamin
  '053': 'KARAFARIN',
  '054': 'PARSIAN',
  '055': 'EGHTESAD_NOVIN',
  '056': 'SAMAN',
  '057': 'PASARGAD',
  '058': 'SARMAYEH',
  '059': 'SINA',
  '060': 'MEHR_IRAN',
  '061': 'SHAHR',
  '062': 'MELLI', // Ayandeh
  '063': 'SEPAH', // Ansar
  '064': 'GARDESHGARI',
  '065': 'SEPAH', // Hekmat Iranian
  '066': 'DAY',
  '069': 'IRAN_ZAMIN',
  '070': 'RESALAT',
  '073': 'SEPAH', // Kowsar
  '075': 'MELAL',
  '078': 'KHAVARMIANEH',
  '079': 'SEPAH', // Mehr Eghtesad
  '080': 'NOOR',
  '095': 'IRAN_VENEZUELA',
};

export function bankFromCard(pan: string): BankCode | null {
  return /^\d{16}$/.test(pan) ? (BINS[pan.slice(0, 6)] ?? null) : null;
}

/** From a stored 6-digit BIN (instrument rows keep the BIN, never the full number in clear). */
export function bankFromBin(bin: string | null | undefined): BankCode | null {
  return bin && /^\d{6}$/.test(bin) ? (BINS[bin] ?? null) : null;
}

export function bankFromIban(iban: string): BankCode | null {
  return /^IR\d{24}$/.test(iban) ? (IBAN_CODES[iban.slice(4, 7)] ?? null) : null;
}

export function isBankCode(v: unknown): v is BankCode {
  return typeof v === 'string' && Object.hasOwn(BANK_NAMES, v);
}

/** Persian bank name for a stored code; null when unknown (the UI then says so honestly). */
export function bankName(code: string | null | undefined): string | null {
  return isBankCode(code) ? BANK_NAMES[code] : null;
}
